import { useState } from "react";
import CloseIcon from "../../../assets/svg/close-icon";
import StarIcon from "../../../assets/svg/star-icon";
import Button from "../../../components/Button/Button";
import { useSendFeedbackMutation } from "../../../redux/services/kamApi";

interface FeedbackFormProps {
  open: boolean;
  onClose: () => void;
}

export const FeedbackForm = ({ open, onClose }: FeedbackFormProps) => {
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>("");

  const [sendFeedback, { isLoading }] = useSendFeedbackMutation();

  const handleClose = () => {
    setRating(0);
    setFeedback("");
    onClose();
  };

  const handleSave = async () => {
    try {
      await sendFeedback({
        rating,
        message: feedback,
      })?.unwrap();
      handleClose();
      setRating(0);
      setFeedback("");
    } catch {
      console.error("Failed to submit feedback");
    }
  };

  const displayRating = hoveredRating || rating;

  return (
    <section
      className={`fixed inset-0 z-100 flex items-center justify-center bg-black/50 backdrop-blur-[1px] ${
        open ? "block" : "hidden"
      }`}
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        className="w-[400px] bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk rounded-lg px-5 py-[18px] shadow-popper-blur-effect backdrop-blur-2xl border border-border dark:border-border-dk"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-6">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-1">
                <span className="text-base text-heading-text dark:text-heading-text-dk">
                  How was you experience?
                </span>
                <p className="text-md font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
                  Your feedback help us to improve and serve you better
                </p>
              </div>
              <button
                className="w-[16px] h-[16px] bg-gray-100 dark:bg-gray-100-dk rounded-full flex flex-col items-center justify-center"
                onClick={handleClose}
              >
                <CloseIcon className="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk" />
              </button>
            </div>

            <div className="flex flex-col gap-[6px]">
              <div className="flex gap-7 items-center">
                <div className="flex gap-3 items-center">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <button
                      key={star}
                      className="w-11 h-11 bg-pure-white dark:bg-pure-white-dk border border-table-header-pinned dark:border-table-header-pinned-dk rounded-xl flex items-center justify-center hover:border-verba-blue-75 dark:hover:border-verba-blue-75-dk transition-all shadow-xs-blur"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                    >
                      <StarIcon
                        className={`w-6 h-6 transition-colors ${
                          star <= displayRating
                            ? "text-rating-yellow dark:text-rating-yellow-dk"
                            : "text-gray-75 dark:text-gray-75-dk"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <span className="text-md font-normal text-gray-placeholder dark:text-gray-placeholder-dk">
                  {displayRating}/5 stars
                </span>
              </div>
              <div className="flex items-center justify-between text-xxs font-normal text-data dark:text-data-dk mr-[92px]">
                <span>Very Poor</span>
                <span>Very Good</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-md font-medium text-heading-text dark:text-heading-text-dk">
              Tell us about your experience (optional)
            </label>
            <textarea
              className="w-full h-[90px] bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg py-2 px-2.5 text-md font-normal text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk resize-none focus:outline-none"
              placeholder="Add feedback (Optional)"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
            />
          </div>

          <div className="flex gap-3 items-center justify-end">
            <Button
              innerText="Cancel"
              buttonContainerClass="w-[100px] px-2 py-[3px] border border-verba-indigo dark:border-verba-indigo-dk rounded-md"
              innerTextClass="text-md font-semibold text-heading-text dark:text-heading-text-dk"
              onClick={handleClose}
              disabled={isLoading}
            />
            <Button
              innerText={isLoading ? "Saving..." : "Save"}
              buttonContainerClass={`w-[100px] px-6 py-[3px] bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk rounded-md transition-all ${
                isLoading ? "opacity-80 cursor-not-allowed" : "hover:opacity-90"
              }`}
              innerTextClass="text-md font-semibold text-pure-white dark:text-pure-white-dk"
              onClick={handleSave}
              isLoading={isLoading}
              disabled={isLoading || rating === 0}
              loaderColor="text-pure-white dark:text-pure-white-dk"
              loaderSize={14}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
